import InstrumentSamplePlayer from "./InstrumentSamplePlayer";
import {getAudioContext} from "../audioContext";

/**
 * InstrumentSamplePlayer represents a single audio sample to be played in an instrument-like fashion.
 * It is meant to have play called over and over again, as needed.
 * This class is responsible for:
 * - making a fetch/ajax call to retrieve audio files, such as mp3, wav, etc
 * - caching the retrieved audioBuffer
 * - playing the audio sample.
 * - providing a release envelope used to control how fast we want releases to occur (initial recording could have a 15 second release, but we want a 1 second release)
 *
 * https://padenot.github.io/web-audio-perf/
 */
export default class InstrumentSampleAsyncPlayer extends InstrumentSamplePlayer {

  async play({volume = 1,}: {volume?: number} ): Promise<Event>{
    const context = getAudioContext();
    this.stopImmediately();//don't allow the sample to be played twice (overlapping)
    this.ensureAudioBufferHasBeenLoaded(this.audioBuffer); //throw an exception if load() hasn't been completed.
    //create the gain we will use as a release envelope.  (when the note is released, we want to ramp down the gain volume over N time)
    this.noteGainNode = this.createConnectedGainNode({destination: this.destination, context, volume});
    //create a new audio source, as once start has been called, it cannot be called again.
    const source = this.createAudioSourceFromAudioBuffer(this.audioBuffer, context);

    //connect to our gain, which should be connected to our destination (passed in by Synthesizer)
    source.connect(this.noteGainNode);
    this.source = source;

    const onEndedPromiseConnected = new Promise<Event>((resolve) => {
      source.addEventListener("ended", ev => resolve(ev));
    });

    // pass start at 0 to force starting when called.
    // without starting at 0 it doesn't seem to always start right away
    source.start(0);
    return onEndedPromiseConnected;
  }


  async schedulePlay({volume = 1, startAtContextRelativeSeconds, durationInSeconds, context=getAudioContext(), destination=this.destination}: {volume?: number; startAtContextRelativeSeconds: number; durationInSeconds: number, context?: AudioContext | OfflineAudioContext, destination?: AudioNode} ): Promise<Event> {
    //create the gain we will use as a release envelope.  (when the note is released, we want to ramp down the gain volume over N time)
    const gainNode = this.createConnectedGainNode({destination, context, volume});
    //create a new audio source, as once start has been called, it cannot be called again.
    const source = this.createAudioSourceFromAudioBuffer(this.audioBuffer, context);
    //connect to our gain, which should be connected to our destination (passed in by Synthesizer)
    source.connect(gainNode);

    const onEndedPromiseConnected = new Promise<Event>((resolve) => {
      source.addEventListener("ended", ev => resolve(ev));
    });
    source.start(startAtContextRelativeSeconds);
    gainNode.gain.setTargetAtTime(0, startAtContextRelativeSeconds + durationInSeconds , this.releaseTimeSeconds / 3);  // no pops in chrome.
    this.scheduledPlaySources.push(source);
    return onEndedPromiseConnected;
  }
}
